import { useState, useEffect, useMemo } from 'react';
import {
  Input, Select, Button, Tag, Modal, Form, InputNumber,
  message, Row, Col, Typography, Space, Tooltip,
  Popconfirm,
} from 'antd';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import {
  PlusOutlined, SearchOutlined, ReloadOutlined, LinkOutlined,
  UserOutlined, DeleteOutlined, EditOutlined,
  ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { talentPoolApi, Candidate, CreateCandidateRequest } from '@/api/talentPoolApi';
import { opportunityApi, type ExtractedRole } from '@/api/opportunityApi';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

const CLEARANCE_OPTIONS = ['SB', 'Secret', 'DVBE', 'Top Secret', 'Public Trust'];
const STATUS_OPTIONS = [
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'ON_PROJECT', label: 'On Project' },
  { value: 'ON_LEAVE', label: 'On Leave' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
];

const statusColors: Record<string, string> = {
  AVAILABLE: '#16A34A',
  ON_PROJECT: '#2563EB',
  ON_LEAVE: '#D97706',
  UNAVAILABLE: '#94A3B8',
};

const statusLabels: Record<string, string> = {
  AVAILABLE: 'Available',
  ON_PROJECT: 'On Project',
  ON_LEAVE: 'On Leave',
  UNAVAILABLE: 'Unavailable',
};

const TalentPoolPage = () => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningCandidate, setAssigningCandidate] = useState<Candidate | null>(null);
  const [opportunities, setOpportunities] = useState<{ id: string; title: string }[]>([]);
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [extractedRoles, setExtractedRoles] = useState<ExtractedRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const loadCandidates = async () => {
    try {
      setLoading(true);
      const data = await talentPoolApi.getAll();
      setCandidates(data);
    } catch {
      message.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const loadOpportunities = async () => {
    try {
      const res = await opportunityApi.getAll({ size: 200 });
      setOpportunities(res.content.map((o) => ({ id: o.id, title: o.title })));
    } catch {
      // silent
    }
  };

  useEffect(() => {
    loadCandidates();
    loadOpportunities();
  }, []);

  const positions = useMemo(() => {
    const set = new Set(candidates.map((c) => c.position));
    return Array.from(set).sort();
  }, [candidates]);

  const filtered = useMemo(() => {
    let result = candidates;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.fullName.toLowerCase().includes(q) ||
          c.position.toLowerCase().includes(q)
      );
    }
    if (positionFilter) {
      result = result.filter((c) => c.position === positionFilter);
    }
    if (statusFilter) {
      result = result.filter((c) => c.availabilityStatus === statusFilter);
    }
    return result;
  }, [candidates, search, positionFilter, statusFilter]);

  const stats = useMemo(() => ({
    total: candidates.length,
    available: candidates.filter((c) => c.availabilityStatus === 'AVAILABLE').length,
    onProject: candidates.filter((c) => c.availabilityStatus === 'ON_PROJECT').length,
    stale: candidates.filter((c) => c.stale).length,
  }), [candidates]);

  const handleCreate = () => {
    setEditingCandidate(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (candidate: Candidate) => {
    setEditingCandidate(candidate);
    form.setFieldsValue({
      fullName: candidate.fullName,
      position: candidate.position,
      availabilityStatus: candidate.availabilityStatus,
      clearances: candidate.clearances,
      dailyRate: candidate.dailyRate,
      email: candidate.email,
      phone: candidate.phone,
      notes: candidate.notes,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const request: CreateCandidateRequest = {
        fullName: values.fullName,
        position: values.position,
        availabilityStatus: values.availabilityStatus || 'AVAILABLE',
        clearances: values.clearances || [],
        dailyRate: values.dailyRate,
        email: values.email,
        phone: values.phone,
        notes: values.notes,
      };
      if (editingCandidate) {
        await talentPoolApi.update(editingCandidate.id, request);
        message.success('Candidate updated');
      } else {
        await talentPoolApi.create(request);
        message.success('Candidate added');
      }
      setModalOpen(false);
      form.resetFields();
      loadCandidates();
    } catch {
      // validation error or API error
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async (id: string) => {
    try {
      await talentPoolApi.refresh(id);
      message.success('Candidate refreshed');
      loadCandidates();
    } catch {
      message.error('Failed to refresh');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await talentPoolApi.delete(id);
      message.success('Candidate removed');
      loadCandidates();
    } catch {
      message.error('Failed to delete');
    }
  };

  const handleAssignOpen = (candidate: Candidate) => {
    setAssigningCandidate(candidate);
    assignForm.resetFields();
    setExtractedRoles([]);
    setAssignModalOpen(true);
  };

  const handleOpportunityChange = async (opportunityId: string) => {
    assignForm.setFieldValue('assignmentRole', undefined);
    setExtractedRoles([]);
    if (!opportunityId) return;
    setLoadingRoles(true);
    try {
      const roles = await opportunityApi.getExtractedRoles(opportunityId);
      setExtractedRoles(roles);
    } catch {
      // no extracted roles yet
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleAssign = async () => {
    try {
      const values = await assignForm.validateFields();
      setSaving(true);
      await talentPoolApi.assign(assigningCandidate!.id, {
        opportunityId: values.opportunityId,
        assignmentRole: values.assignmentRole,
        notes: values.notes,
      });
      message.success('Candidate assigned to RFO');
      setAssignModalOpen(false);
      loadCandidates();
    } catch (err: any) {
      if (err?.response?.status === 500) {
        message.error('Candidate is already assigned to this opportunity');
      }
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#2563EB', '#7C3AED', '#0891B2', '#059669', '#D97706', '#DC2626'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Header */}
      <PageHeader
        title="Talent Pool"
        subtitle={`${stats.total} candidate${stats.total !== 1 ? 's' : ''} · ${stats.available} available · ${filtered.length} shown`}
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size="large">
            Add Candidate
          </Button>
        }
      />

      {/* Stat Cards */}
      <Row gutter={16} style={{ marginBottom: 20 }} className="ui-stagger">
        {[
          { label: 'Total Candidates', value: stats.total, color: 'var(--color-text-primary)', icon: <UserOutlined /> },
          { label: 'Available', value: stats.available, color: 'var(--color-success)', icon: <CheckCircleOutlined /> },
          { label: 'On Project', value: stats.onProject, color: 'var(--color-primary)', icon: <ClockCircleOutlined /> },
          { label: 'Stale Records', value: stats.stale, color: 'var(--color-warning)', icon: <ExclamationCircleOutlined /> },
        ].map((s) => (
          <Col span={6} key={s.label}>
            <div className="ui-surface" style={{ padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: 'var(--gradient-brand-soft)', color: s.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 17,
                }}>
                  {s.icon}
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <div className="ui-toolbar">
        <Input
          prefix={<SearchOutlined style={{ color: 'var(--color-text-tertiary)' }} />}
          placeholder="Search by name or position..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 260 }}
          allowClear
        />

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="ui-chip ui-focusable"
            data-active={!statusFilter}
            onClick={() => setStatusFilter(undefined)}
          >
            All
          </button>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className="ui-chip ui-focusable"
              data-active={statusFilter === opt.value}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        <Select
          placeholder="All positions"
          value={positionFilter}
          onChange={setPositionFilter}
          allowClear
          className="filter-select"
          style={{ minWidth: 180 }}
          options={positions.map((p) => ({ value: p, label: p }))}
        />
      </div>

      {/* Cards Grid */}
      {loading ? (
        <Row gutter={[16, 16]}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Col key={i} xs={24} sm={12} lg={8} xl={6}>
              <div className="ui-surface" style={{ padding: 18 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
                  <div className="ui-skeleton" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div className="ui-skeleton" style={{ width: '70%', height: 14, marginBottom: 8 }} />
                    <div className="ui-skeleton" style={{ width: '45%', height: 11 }} />
                  </div>
                </div>
                <div className="ui-skeleton" style={{ width: 80, height: 20, borderRadius: 6, marginBottom: 12 }} />
                <div className="ui-skeleton" style={{ width: '100%', height: 11, marginBottom: 6 }} />
                <div className="ui-skeleton" style={{ width: '60%', height: 11 }} />
              </div>
            </Col>
          ))}
        </Row>
      ) : filtered.length === 0 ? (
        <div className="ui-surface">
          <EmptyState
            icon={<UserOutlined />}
            title={search || positionFilter || statusFilter ? 'No matching candidates' : 'No candidates yet'}
            description={
              search || positionFilter || statusFilter
                ? 'Try clearing filters or adjusting your search.'
                : 'Add your first candidate to start building your talent pool.'
            }
            action={
              search || positionFilter || statusFilter ? (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => { setSearch(''); setPositionFilter(undefined); setStatusFilter(undefined); }}
                >
                  Clear filters
                </Button>
              ) : (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                  Add Candidate
                </Button>
              )
            }
          />
        </div>
      ) : (
        <Row gutter={[16, 16]} className="ui-stagger">
          {filtered.map((c) => (
            <Col key={c.id} xs={24} sm={12} lg={8} xl={6}>
              <CandidateCard
                candidate={c}
                onRefresh={() => handleRefresh(c.id)}
                onEdit={() => handleEdit(c)}
                onDelete={() => handleDelete(c.id)}
                onAssign={() => handleAssignOpen(c)}
                getInitials={getInitials}
                getAvatarColor={getAvatarColor}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Create / Edit Modal */}
      <Modal
        title={editingCandidate ? 'Edit Candidate' : 'Add Candidate'}
        open={modalOpen}
        maskClosable={false}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={handleSave}
        confirmLoading={saving}
        okText={editingCandidate ? 'Update' : 'Add'}
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fullName" label="Full Name" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="John Doe" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="position" label="Position" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Cloud Architect" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="availabilityStatus" label="Status" initialValue="AVAILABLE">
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dailyRate" label="Daily Rate ($)">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="850" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="clearances" label="Clearances">
            <Select mode="multiple" placeholder="Select clearances" options={CLEARANCE_OPTIONS.map((c) => ({ value: c, label: c }))} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email">
                <Input placeholder="john@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="(555) 123-4567" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Additional notes..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign to RFO Modal */}
      <Modal
        title={`Assign ${assigningCandidate?.fullName || ''} to RFO`}
        open={assignModalOpen}
        maskClosable={false}
        onCancel={() => setAssignModalOpen(false)}
        onOk={handleAssign}
        confirmLoading={saving}
        okText="Assign"
        width={480}
      >
        <Form form={assignForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="opportunityId" label="Select Opportunity" rules={[{ required: true, message: 'Select an opportunity' }]}>
            <Select
              showSearch
              placeholder="Search opportunities..."
              filterOption={(input, option) =>
                (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
              }
              options={opportunities.map((o) => ({ value: o.id, label: o.title }))}
              onChange={handleOpportunityChange}
            />
          </Form.Item>
          <Form.Item name="assignmentRole" label="Resource Role (optional — from AI-extracted requirements)">
            <Select
              showSearch
              allowClear
              placeholder={loadingRoles ? 'Loading roles...' : extractedRoles.length > 0 ? 'Select role...' : 'No roles extracted yet — you can assign without a role'}
              loading={loadingRoles}
              filterOption={(input, option) =>
                (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
              }
              options={extractedRoles.map((r) => ({
                value: r.roleTitle,
                label: `${r.roleTitle}${r.count > 1 ? ` (×${r.count})` : ''}${r.classification ? ` — ${r.classification}` : ''}`,
              }))}
              notFoundContent={loadingRoles ? 'Loading...' : 'No AI-extracted roles yet. You can still assign without a role.'}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea rows={2} placeholder="Assignment notes..." />
          </Form.Item>
        </Form>
        {assigningCandidate && assigningCandidate.assignments.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Currently assigned to:</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {assigningCandidate.assignments.map((a, idx) => (
                <Tag key={idx} color="blue" style={{ fontSize: 11 }}>
                  {a.opportunityTitle}
                  {a.assignmentRole && <span style={{ opacity: 0.7 }}> ({a.assignmentRole})</span>}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

/* ─── Candidate Card Component ─── */
interface CandidateCardProps {
  candidate: Candidate;
  onRefresh: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAssign: () => void;
  getInitials: (name: string) => string;
  getAvatarColor: (name: string) => string;
}

const CandidateCard = ({
  candidate: c,
  onRefresh,
  onEdit,
  onDelete,
  onAssign,
  getInitials,
  getAvatarColor,
}: CandidateCardProps) => {
  return (
    <div
      className="ui-surface ui-surface--interactive"
      style={{
        padding: 18,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'default',
      }}
    >
      {/* Top: Avatar + Name + Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, minWidth: 0 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: getAvatarColor(c.fullName),
            color: 'var(--color-text-inverse)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, flexShrink: 0,
          }}>
            {getInitials(c.fullName)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.fullName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.position}
            </div>
          </div>
        </div>
        <Tooltip title="Edit">
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={onEdit}
            style={{ color: 'var(--color-text-tertiary)' }}
          />
        </Tooltip>
      </div>

      {/* Status + Stale Badge */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <Tag
          color={statusColors[c.availabilityStatus]}
          style={{ borderRadius: 6, fontSize: 11, margin: 0, fontWeight: 500 }}
        >
          {statusLabels[c.availabilityStatus] || c.availabilityStatus}
        </Tag>
        {c.stale && (
          <Tag color="warning" style={{ borderRadius: 6, fontSize: 11, margin: 0 }}>
            <ExclamationCircleOutlined style={{ marginRight: 3 }} />Stale
          </Tag>
        )}
      </div>

      {/* Clearances */}
      {c.clearances && c.clearances.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 3 }}>Clearances</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {c.clearances.map((cl) => (
              <Tag key={cl} style={{ borderRadius: 4, fontSize: 11, margin: 0, background: 'var(--color-bg-hover)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                {cl}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Daily Rate */}
      {c.dailyRate != null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Daily Rate</Text>
          <Text strong style={{ fontSize: 14, color: 'var(--color-text-primary)' }}>${c.dailyRate.toLocaleString()}/day</Text>
        </div>
      )}

      {/* Assignments */}
      {c.assignments && c.assignments.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 3 }}>Assigned to</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {c.assignments.map((a, idx) => (
              <Tooltip key={idx} title={`${a.opportunityTitle}${a.assignmentRole ? ' - ' + a.assignmentRole : ''}`}>
                <Tag color="blue" style={{ borderRadius: 4, fontSize: 11, margin: 0 }}>
                  {a.opportunityTitle.length > 15 ? a.opportunityTitle.slice(0, 15) + '...' : a.opportunityTitle}
                  {a.stageDisplayName && (
                    <span style={{ opacity: 0.7 }}> / {a.stageDisplayName.split(' ')[0]}</span>
                  )}
                </Tag>
              </Tooltip>
            ))}
          </div>
        </div>
      )}

      {/* Footer: Updated date + actions */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        paddingTop: 10, borderTop: '1px solid var(--color-border-light)', marginTop: 'auto',
      }}>
        <Tooltip title={c.updatedAt ? dayjs(c.updatedAt).format('MMM D, YYYY h:mm A') : ''}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <ClockCircleOutlined style={{ marginRight: 3 }} />
            {c.updatedAt ? dayjs(c.updatedAt).fromNow() : 'N/A'}
          </Text>
        </Tooltip>
        <Space size={4}>
          <Tooltip title="Refresh">
            <Button type="text" size="small" icon={<ReloadOutlined />} style={{ color: 'var(--color-text-secondary)', fontSize: 12 }} onClick={onRefresh} />
          </Tooltip>
          <Tooltip title="Assign to RFO">
            <Button type="text" size="small" icon={<LinkOutlined />} style={{ color: 'var(--color-primary)', fontSize: 12 }} onClick={onAssign} />
          </Tooltip>
          <Popconfirm title="Delete this candidate?" onConfirm={onDelete} okText="Delete" okButtonProps={{ danger: true }}>
            <Tooltip title="Delete">
              <Button type="text" size="small" icon={<DeleteOutlined />} style={{ color: 'var(--color-error)', fontSize: 12 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      </div>
    </div>
  );
};

export default TalentPoolPage;
